import { useCallback, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';

import { BoExportKind, BoExportResponse } from '@Common/types/bo/exports';

import { getErrorMessage } from '@sh4res/bo-common/utils';

export type ServerExportBody = { kind: BoExportKind } & Record<string, unknown>;

type PostAction = (resource: string, data: ServerExportBody) => Promise<{ data: BoExportResponse }>;

/**
 * Requests an export the backend builds, from `POST <list resource>/export`, and downloads it.
 *
 * The request waits for the file: clicking again with the same parameters, after a timeout, waits
 * for the same file rather than building another one.
 */
export function useServerExport() {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [isExporting, setIsExporting] = useState(false);

  const exportFile = useCallback(
    async (resource: string, body: ServerExportBody) => {
      const postAction = dataProvider.postAction as PostAction;
      setIsExporting(true);

      try {
        const { data } = await postAction(`${resource}/export`, body);
        downloadFromUrl(data.document_url, data.file_name);
      } catch (error: unknown) {
        notify('resources.bo_common.export.failure', {
          type: 'error',
          messageArgs: { error: getErrorMessage(error) },
          multiLine: true,
        });
      } finally {
        setIsExporting(false);
      }
    },
    [dataProvider, notify],
  );

  return { exportFile, isExporting };
}

/** The URL answers with an attachment, so following it downloads the file without leaving */
function downloadFromUrl(url: string, fileName: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
}
